import { SCRIBE_LANGUAGE_LABELS } from '@behuman/core-contract'
import { useCallSession } from '../session'
import { scribeText } from './scribeText'

/**
 * `<SessionStatusBar>` — always-on Provider / Patient / Scribe
 * status row inside `<ZoomPanel>` (PD-1067 item 7). Presence + scribe state come
 * from `useCallSession()`. Dark-panel styling to match the rail.
 */
type Dot = { label: string; value: string; color: string }

const DOT_COLORS = {
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444',
  gray: '#64748b',
} as const

export function SessionStatusBar() {
  const {
    isJoined,
    patientPresence,
    staffPresence,
    scribeState,
    isLocalUserManager,
    encounterLanguage,
  } = useCallSession()

  const patient: Dot =
    patientPresence === 'connected'
      ? { label: 'Patient', value: 'Connected', color: DOT_COLORS.green }
      : patientPresence === 'disconnected'
        ? { label: 'Patient', value: 'Disconnected', color: DOT_COLORS.red }
        : { label: 'Patient', value: 'Not joined', color: DOT_COLORS.amber }

  const scribe: Dot =
    scribeState === 'streaming'
      ? { label: 'Scribe', value: 'On', color: DOT_COLORS.green }
      : scribeState === 'paused'
        ? { label: 'Scribe', value: 'Paused', color: DOT_COLORS.amber }
        : { label: 'Scribe', value: 'Ready', color: DOT_COLORS.gray }

  // Coordinator dot appears only once a care coordinator joins, so 2-party
  // calls render exactly as before.
  const coordinator: Dot | null =
    staffPresence === 'connected'
      ? { label: 'Coordinator', value: 'Connected', color: DOT_COLORS.green }
      : staffPresence === 'disconnected'
        ? { label: 'Coordinator', value: 'Disconnected', color: DOT_COLORS.red }
        : null

  const dots: Dot[] = [
    {
      label: 'Provider',
      value: isJoined ? 'Connected' : 'Off',
      color: isJoined ? DOT_COLORS.green : DOT_COLORS.gray,
    },
    patient,
    ...(coordinator ? [coordinator] : []),
    ...(isLocalUserManager === true ? [] : [scribe]),
    // PD-1600 — only a non-English visit is worth a dot; English is the norm.
    ...(encounterLanguage !== 'en'
      ? [
          {
            label: 'Language',
            value: SCRIBE_LANGUAGE_LABELS[encounterLanguage],
            color: DOT_COLORS.amber,
          },
        ]
      : []),
  ]

  return (
    <div
      data-testid="session-status-bar"
      className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-white/[0.07] px-[14px] py-[7px]"
    >
      {dots.map((d) => (
        <span
          key={d.label}
          className={`flex items-center gap-[5px] ${scribeText.meta} text-slate-400`}
        >
          <span
            aria-hidden="true"
            className="h-[6px] w-[6px] rounded-full"
            style={{ background: d.color }}
          />
          <span className={`${scribeText.meta} font-medium text-slate-500`}>{d.label}:</span>
          <span className={`${scribeText.meta} text-slate-300`}>{d.value}</span>
        </span>
      ))}
    </div>
  )
}
